import {
  CircularProgress,
  Fab,
  FormControl,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { updateTestDriveBooking } from "../../../../actions/testDriveBooking/actions";
import { TestDriveBookingSteps } from "../../../../reducers/testDriveBooking/types";
import { TestDriveBookingStepper } from "./TestDriveStepper";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Formik, Form, Field, FieldProps } from "formik";
import { DateTime as d } from "luxon";
import { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { ApolloError } from "apollo-boost";
import { CREATE_BOOKING } from "../../../../graphql/bookings/createBookingMutation";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { buildFieldRequiredMap, buildFieldVisibilityMap, defaultWebsiteIndividualFields, formatGraphQLErrorMessage } from "../../../utils";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { useHistory } from "react-router-dom";
import { BookingType, IAddressLocation } from "../../../../reducers/bookings/types";
import { IDriverLicense, IPhoneNumber, IUser } from "../../../../reducers/auth/types";
import { testDriveBookingInitialState } from "../../../../reducers/testDriveBooking/consts";
import { CREATE_CUSTOMER } from "../../../../graphql/customers/createCustomerMutation";
import { IAddress } from "../../../../reducers/website/types";
import AddressPicker from "../../../AddressPicker/AddressPicker";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { StyledInput } from "../../CustomerPortal/Invoices";
import {
  getDefaultCountryCode,
  getLocalisedLicense,
  getLocalizedBookingSyntex,
} from "../../../../utils/localized.syntex";
import * as Yup from "yup";
import { RATE_TYPES } from "../../utils";
import { BookingStatus } from "../../../../reducers/bookings/types";
import { UPDATE_BOOKING } from "../../../../graphql/bookings/updateBookingMutation";
import { useTranslation } from "react-i18next";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../../graphql/customers/getConsumerCustomForm";
import { TranslationKeys } from "../../../../utils/translations/types";
interface IPersonalDetails {
  firstName: string;
  lastName: string;
  address: IAddress;
  emailId: string;
  phoneNumber: IPhoneNumber;
  license: IDriverLicense;
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
  },
  formControl: {
    width: "100%",
  },
  title: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  select: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 6,
    },
    padding: 3,
  },
  input: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 6,
    },
    "& .MuiOutlinedInput-root": {
      padding: 4,
    },
  },
  text: { marginTop: theme.spacing(1.5), marginBottom: theme.spacing(0.5) },
  addressText: {
    marginTop: theme.spacing(1.5),
  },
  proceedIcon: {
    marginRight: theme.spacing(1),
  },
  proceedButton: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    borderRadius: 0,
  },
}));

const initialPersonalDetails: IPersonalDetails = {
  firstName: "",
  lastName: "",
  address: {
    country: "",
  },
  emailId: "",
  phoneNumber: {
    phone: "",
    country: "",
  },
  license: {
    licenseNumber: "",
    authority: "",
    dateOfExpiry: "",
    dvlaVerificationStatus: "",
    endorsement: "",
    images: [],
    offences: 0,
    points: 0
  }
};

const prepareBookingInput = (
  values: any,
  customerId: string,
  userState?: IUser,
  bookingUpdate: boolean = false,
  pickupOtherLocation?: IAddress
) => {
  const transformAddressForBookingCreation = (address?: IAddress) => {
    if (!address) return undefined;
    const location = address.geometry ? {
      type: address.geometry.type || "",
      latitude: address.geometry.latitude || 0,
      longitude: address.geometry.longitude || 0
    } : {
      type: "",
      latitude: 0,
      longitude: 0
    };
    return {
      street: address.street || "",
      city: address.city || "",
      fullAddress: address.fullAddress || "",
      state: address.state || "",
      country: address.country || "",
      zipcode: address.zipcode || "",
      location
    };
  };

  const bookingInput: any = {
    pickupServiceLocation: values.pickupLocation,
    pickupOtherLocation: transformAddressForBookingCreation(pickupOtherLocation),
    pickupDateTime: values.pickupDate,
    dropoffDateTime: values.dropoffDate,
    dropoffServiceLocation: values.pickupLocation,
    vehicleGroups: [
      {
        vehicleGroup: values.vehicleGroupId,
        vehicleIds: [values.availableVehicleIds[0]],
        baseRate: 0,
        deposit: 0,
        mileageLimit: 0,
        unlimitedMileage: false,
        pricePerExtraMile: 0,
        count: 0,
      },
    ],
    insuranceRate: 0,
    excess: 0,
    addonRequirements: [],
    rateTypeName: RATE_TYPES.DAILY,
    rateTypeDuration: 1440,
    locationSurcharges: [],
    bookingCategory: BookingType.TEST_DRIVE,
    testDriveBookingDetails: values.testDriveBookingDetails,
    dropoffSlot: values.dropoffSlot,
    pickupSlot: values.pickupSlot
  };

  if (userState?.businesscustomers?.id) {
    bookingInput.businessCustomer = customerId;
  } else {
    bookingInput.customer = customerId;
  }

  if (bookingUpdate) {
    bookingInput.id = values.id;
    bookingInput.pcoNumber = false;
    bookingInput.longTermBooking = false;
    bookingInput.approvedDrivers = [];
    delete bookingInput.customer;
    delete bookingInput.businessCustomer;
    delete bookingInput.testDriveBookingDetails;
  }

  return bookingInput;
};

export const PersonalDetails: React.FC = () => {
  const classes = useStyles();
  const testDriveBookingState = useSelector(
    (state: IAppState) => state.testDriveBookingReducer
  );
  const userState = useSelector((state: IAppState) => state.authReducer.user);

  const dispatch = useDispatch();
  const snackbar = useSnackBar();
  const history = useHistory();

  const [personalDetails, setPersonalDetails] = useState<IPersonalDetails>(
    initialPersonalDetails
  );
  const [customerId, setCustomerId] = useState<string>("");
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const organisationData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const { country } = website.organisation.address;
  const { t } = useTranslation();
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(
    buildFieldRequiredMap(defaultWebsiteIndividualFields)
  );
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    buildFieldVisibilityMap(defaultWebsiteIndividualFields)
  );
  const [createCustomer, { loading: creatingCustomer }] = useMutation(
    CREATE_CUSTOMER,
    {
      onCompleted: ({ consumerCreateCustomer }) => {
        const bookingInput = prepareBookingInput(
          testDriveBookingState,
          consumerCreateCustomer.id,
          userState,
          false,
          testDriveBookingState?.pickupOtherLocation
        );
        createBookingMutation({
          variables: {
            branchId: testDriveBookingState.branchId,
            booking: bookingInput,
          },
        });
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR,
        });
      },
    }
  );

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {

      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        const customVisibilityMap = buildFieldVisibilityMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
        setVisibilityMap(customVisibilityMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
        const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
        setRequiredMap(defaultMap);
        setVisibilityMap(defaultVisibilityMap);
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
      const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
      setRequiredMap(defaultMap);
      setVisibilityMap(defaultVisibilityMap);
    }
  });

  useEffect(() => {
    getCustomFormByType({
      variables: {
        organisationId: organisationData.id,
        formType: "INDIVIDUAL",
        atBranchLevel: false
      }
    })
  }, [])

  useEffect(() => {
    if (userState && userState.id && userState.customerId) {
      setCustomerId(userState.customerId);
      setPersonalDetails({
        ...personalDetails,
        firstName: userState.firstName,
        lastName: userState.lastName,
        emailId: userState.email,
        phoneNumber: userState.phoneNumber,
        license: userState.customer?.license ?? initialPersonalDetails.license,
      });
    } else if (userState && userState.id && userState.businesscustomers) {
      setCustomerId(userState.businesscustomers.id);
      setPersonalDetails({
        ...personalDetails,
        firstName: userState.businesscustomers.contact.firstName,
        lastName: userState.businesscustomers.contact.lastName,
        emailId: userState.businesscustomers.contact.email,
        phoneNumber: userState.businesscustomers.contact.phoneNumber,
      });
    }
  }, [userState]);

  const [createBookingMutation, { loading: bookingCreating }] = useMutation(
    CREATE_BOOKING,
    {
      onCompleted: (data) => {
        history.push(`/test-drive-booking?id=${data.consumerCreateBooking.id}`);
        dispatch(
          updateTestDriveBooking({
            ...testDriveBookingInitialState,
          })
        );
        snackbar({
          message: t("root:testDriveRequested"),
          variant: SnackBarVariant.SUCCESS,
        });
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR,
        });
      },
    }
  );

  const [updateBookingMutation, { loading: updateBookingLoading }] =
    useMutation(UPDATE_BOOKING, {
      onCompleted: ({ consumerUpdateBooking }) => {
        if (consumerUpdateBooking.status === BookingStatus.CONFIRMED) {
          history.push(`/account/bookings/${consumerUpdateBooking.id}`);
        }
        snackbar({
          message: `${t("root:testDrive")} ${getLocalizedBookingSyntex(country)} ${t("root:updated")}`,
          variant: SnackBarVariant.SUCCESS,
        });
      },
      onError: (error: ApolloError) =>
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR,
        }),
    });

  const nextStep = (values: IPersonalDetails) => {
    const customerDetails = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.emailId,
      phoneNumber: values.phoneNumber,
      location: values.address,
      license: values.license,
    };

    if (testDriveBookingState.isBookingUpdate) {
      const bookingInput = prepareBookingInput(
        testDriveBookingState,
        customerId,
        userState,
        testDriveBookingState.isBookingUpdate
      );
      updateBookingMutation({
        variables: {
          branchId: testDriveBookingState.branchId,
          booking: bookingInput,
        },
      });
    } else if (customerId) {
      const bookingInput = prepareBookingInput(
        testDriveBookingState,
        customerId,
        userState,
        false,
        testDriveBookingState?.pickupOtherLocation,
      );
      createBookingMutation({
        variables: {
          branchId: testDriveBookingState.branchId,
          booking: bookingInput,
        },
      });
    } else {
      createCustomer({
        variables: {
          customer: customerDetails,
        },
      });
    }
  };

  const prevStep = () => {
    dispatch(
      updateTestDriveBooking({
        ...testDriveBookingState,
        currentStep: TestDriveBookingSteps.VEHICLE_SELECTION,
      })
    );
  };

  const createCustomerValidationSchema = (
    requiredMap: Record<string, boolean>,
    customerId?: string | null // to control address validation
  ) => {
    const createConditionalString = (
      key: string,
      errorKey: TranslationKeys,
      interpolationValues?: Record<string, string | number>
    ) =>
      requiredMap[key]
        ? interpolationValues
          ? Yup.string().required(t(errorKey, { ...interpolationValues }))
          : Yup.string().required(t(errorKey))
        : Yup.string();

    const createConditionalEmail = (key: string, requiredErrorKey: TranslationKeys, invalidErrorKey: TranslationKeys) => {
      const base = Yup.string().email(t(invalidErrorKey));
      return requiredMap[key] ? base.required(t(requiredErrorKey)) : base;
    };

    const createConditionalPhone = (key: string, errorKey: TranslationKeys) => {
      const base = Yup.string().test("is-valid-phone", t("root:invalidPhoneNumber"), (value) =>
        !value ? true : isValidPhoneNumber(value)
      );
      return requiredMap[key] ? base.required(t(errorKey)) : base;
    };

    const createAddressSchema = (required: boolean) => {
      const shape = {
        fullAddress: required ? Yup.string().required(t("validation:addressRequired")) : Yup.string(),
        country: required ? Yup.string().required(t("validation:registeredAddressCountryRequired")) : Yup.string(),
        state: required ? Yup.string().required(t("validation:registeredAddressStateRequired")) : Yup.string(),
        city: required ? Yup.string().required(t("validation:tradingAddressCityRequired")) : Yup.string(),
        street: required ? Yup.string().required(t("validation:tradingAddressStreetRequired")) : Yup.string(),
      };

      return required ? Yup.object().shape(shape).required(t("validation:addressRequired")) : Yup.object().shape(shape);
    };

    return Yup.object().shape({
      emailId: createConditionalEmail("email", "validation:emailIsRequired", "validation:enterValidEmailAddress"),

      phoneNumber: Yup.object().shape({
        phone: createConditionalPhone("phoneNumber", "validation:phoneNumberRequired"),
      }),

      firstName: createConditionalString("firstName", "validation:firstNameRequired"),
      lastName: createConditionalString("lastName", "validation:lastNameRequired"),

      address: !customerId && requiredMap.location
        ? createAddressSchema(true)
        : createAddressSchema(false),

      license: Yup.object().shape({
        licenseNumber: createConditionalString(
          "licenseNumber",
          "validation:licenceNumberRequired",
          { licenceSyntax: getLocalisedLicense(country) }
        ),
      }),
    });
  };
  const schema = createCustomerValidationSchema(requiredMap, customerId);


  const firstNamePlaceholder = requiredMap.firstName
    ? `${t('root:firstName')} *`
    : t('root:firstName');

  const lastNamePlaceholder = requiredMap.lastName
    ? `${t("root:lastName")} *`
    : t("root:lastName");

  const emailPlaceholder = requiredMap.email
    ? `${t("root:email")} *`
    : t("root:email");

  const licenseNumberPlaceholder = requiredMap.licenseNumber
    ? `${t("root:enterDrivingLicenceNumber")} *`
    : t("root:enterDrivingLicenceNumber");

  return (
    <Grid item container spacing={2}>
      <Grid
        item
        container
        xs={12}
        spacing={1}
        alignItems="flex-start"
        justify="flex-start"
      >
        <Grid item>
          <IconButton aria-label="previous-step" onClick={prevStep}>
            <ArrowBackIcon style={{ padding: 0 }} />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <Typography variant="h1" component={"h1"}>
            {t("root:personalDetails")}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <TestDriveBookingStepper />
      </Grid>
      <Grid container item xs={12}>
        <Formik
          validationSchema={schema}
          initialValues={personalDetails}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            nextStep(values);
            setSubmitting(false);
          }}
        >
          {(formikProps) => (
            <Form className={classes.form}>
              <Grid container item xs={12} spacing={2}>
                {visibilityMap.firstName && (
                <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                  <FormControl variant="outlined" className={classes.formControl} fullWidth>
                    <Typography variant="body1" className={classes.text}>
                      {t("root:firstNameLabel")}
                    </Typography>
                    <Field name="firstName">
                      {({ field, meta }: FieldProps) => (
                        <>
                          <TextField
                            {...field}
                            id="first-name-input"
                            disabled={Boolean(customerId)}
                            placeholder={firstNamePlaceholder}
                            className={classes.input}
                            variant="outlined"
                            fullWidth
                          />
                          {meta.touched && meta.error && (
                            <Typography variant="body2" color="error">
                              {meta.error}
                            </Typography>
                          )}
                        </>
                      )}
                    </Field>
                  </FormControl>
                </Grid>
                )}
                {visibilityMap.lastName && (
                <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                  <FormControl variant="outlined" className={classes.formControl} fullWidth>
                    <Typography variant="body1" className={classes.text}>
                      {t("root:lastNameLabel")}
                    </Typography>
                    <Field name="lastName">
                      {({ field, meta }: FieldProps) => (
                        <>
                          <TextField
                            {...field}
                            id="last-name-input"
                            disabled={Boolean(customerId)}
                            placeholder={lastNamePlaceholder}
                            className={classes.input}
                            variant="outlined"
                            fullWidth
                          />
                          {meta.touched && meta.error && (
                            <Typography variant="body2" color="error">
                              {meta.error}
                            </Typography>
                          )}
                        </>
                      )}
                    </Field>
                  </FormControl>
                </Grid>
                )}
                {visibilityMap.email && (
                <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                  <FormControl variant="outlined" className={classes.formControl} fullWidth>
                    <Typography variant="body1" className={classes.text}>
                      {t("root:emailLabel")}
                    </Typography>
                    <Field name="emailId">
                      {({ field, meta }: FieldProps) => (
                        <>
                          <TextField
                            {...field}
                            id="email-input"
                            disabled={Boolean(customerId)}
                            placeholder={emailPlaceholder}
                            className={classes.input}
                            variant="outlined"
                            fullWidth
                          />
                          {meta.touched && meta.error && (
                            <Typography variant="body2" color="error">
                              {meta.error}
                            </Typography>
                          )}
                        </>
                      )}
                    </Field>
                  </FormControl>
                </Grid>
                )}
                {visibilityMap.location && !customerId && (
                  <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <Typography
                        variant="body1"
                        className={classes.addressText}
                      >
                        {`${t("root:address")} ${requiredMap.location ? " *" : ""}`}
                      </Typography>
                      <AddressPicker
                        fieldName={"address"}
                        required={!customerId && requiredMap.location ? true : false}
                        onChange={(address: IAddress | undefined) => {
                          if (address && address.fullAddress) {
                            formikProps.setFieldValue("address", address);
                          } else {
                            formikProps.setFieldValue("address", {
                              street: "",
                              city: "",
                              country: "",
                              fullAddress: "",
                              state: "",
                              zipcode: "",
                            });
                          }
                        }}
                      />
                      {formikProps.touched.address && (
                        <>
                          <Typography variant="body2" color="error">
                            {formikProps.touched.address.fullAddress &&
                              Boolean(
                                formikProps.errors.address?.fullAddress
                              ) &&
                              formikProps.errors.address?.fullAddress}
                          </Typography>
                        </>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {visibilityMap.phoneNumber && (
                <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    required={requiredMap.phoneNumber}
                  >
                    <Typography variant="body1" className={classes.text}>
                      {`${t("root:mobileNumber")} ${requiredMap.phoneNumber ? " *" : ""}`}
                    </Typography>
                    <Field component={StyledInput} name={"phoneNumber.phone"}>
                      {({ field, meta: { touched, error } }: FieldProps) => {
                        return (
                          <div>
                            <div
                              style={{
                                border: `1px solid ${touched && Boolean(error)
                                  ? "#D13135"
                                  : "#c0c0c0"
                                  }`,
                                borderRadius: 6,
                              }}
                            >
                              <PhoneInput
                                {...field}
                                disabled={Boolean(customerId)}
                                international={false}
                                defaultCountry={getDefaultCountryCode(
                                  website.organisation.address.country
                                )}
                                placeholder={`${t("root:phonePlaceholder")} ${requiredMap.phoneNumber ? " *" : ""}`}
                                className={
                                  touched && Boolean(error)
                                    ? "PhoneInputInputError"
                                    : ""
                                }
                                name={"phoneNumber.phone"}
                                onChange={(val) => {
                                  formikProps.setFieldValue(
                                    "phoneNumber.phone",
                                    val
                                  );
                                }}
                                onCountryChange={(val) => {
                                  formikProps.setFieldValue(
                                    "phoneNumber.country",
                                    val
                                  );
                                }}
                              />
                            </div>
                            <Typography variant="body2" color="error">
                              {touched && Boolean(error) && error}
                            </Typography>
                          </div>
                        );
                      }}
                    </Field>
                  </FormControl>
                </Grid>
                )}
                {visibilityMap.licenseNumber && requiredMap.licenseNumber && (
                  <Grid item xs={6} sm={4} md={4} lg={4} xl={4}>
                    <FormControl variant="outlined" className={classes.formControl} fullWidth>
                      <Typography variant="body1" className={classes.text}>
                        {t("root:drivingLicenseNumber", { licenceSyntax: getLocalisedLicense(country) })}
                      </Typography>
                      <Field name="license.licenseNumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <TextField
                              {...field}
                              id="license-number-input"
                              name="license.licenseNumber"
                              required={requiredMap.licenseNumber}
                              disabled={Boolean(customerId)}
                              placeholder={licenseNumberPlaceholder}
                              value={formikProps.values.license.licenseNumber}
                              onChange={formikProps.handleChange}
                              className={classes.input}
                              variant="outlined"
                            />
                            {touched && Boolean(error) && (
                              <Typography variant="body2" color="error">
                                {error}
                              </Typography>
                            )}
                          </>
                        )}
                      </Field>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl variant="outlined">
                    <Grid item xs={12}>
                      <Fab
                        aria-label="request-test-drive"
                        variant="extended"
                        size="medium"
                        type="submit"
                        className={classes.proceedButton}
                        style={{
                          backgroundColor:
                            creatingCustomer ||
                              bookingCreating ||
                              updateBookingLoading
                              ? "#e0e0e0"
                              : "var(--primary-color)",
                        }}
                        disabled={
                          creatingCustomer ||
                          bookingCreating ||
                          updateBookingLoading
                        }
                      >
                        {bookingCreating ||
                          creatingCustomer ||
                          updateBookingLoading ? (
                          <CircularProgress
                            size={20}
                            className={classes.proceedIcon}
                          />
                        ) : (
                          <ArrowForwardIcon className={classes.proceedIcon} />
                        )}
                        {t("root:requestTestDrive")}
                      </Fab>
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};
